import { useState, useEffect } from "react";
import { getCatalogItems, createCatalogItem, updateCatalogItem, deleteCatalogItem } from "../services/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from "../components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Plus, Search, Package, Wrench, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

const UNIT_LABELS = {
  hour: "Heure",
  day: "Jour",
  flat: "Forfait",
  piece: "Pièce",
  month: "Mois",
  project: "Projet"
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR"
  }).format(amount);
};

const CatalogPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    item_type: "service",
    name: "",
    description: "",
    unit: "flat",
    unit_price_ht: 0,
    vat_rate: 20
  });

  useEffect(() => {
    loadItems();
  }, [search, filterType]);

  const loadItems = async () => {
    try {
      const res = await getCatalogItems({ 
        search: search || undefined,
        item_type: filterType && filterType !== "all" ? filterType : undefined 
      });
      setItems(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement du catalogue");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        item_type: item.item_type,
        name: item.name,
        description: item.description || "",
        unit: item.unit,
        unit_price_ht: item.unit_price_ht,
        vat_rate: item.vat_rate
      });
    } else {
      setEditingItem(null);
      setForm({
        item_type: "service",
        name: "",
        description: "",
        unit: "flat",
        unit_price_ht: 0,
        vat_rate: 20
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Le nom est obligatoire");
      return;
    }
    
    try {
      if (editingItem) {
        await updateCatalogItem(editingItem.id, form);
        toast.success("Article mis à jour");
      } else {
        await createCatalogItem(form);
        toast.success("Article créé");
      }
      setDialogOpen(false);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Archiver cet article ?")) return;
    try {
      await deleteCatalogItem(id);
      toast.success("Article archivé");
      loadItems();
    } catch (err) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  return (
    <div className="p-4 lg:p-6 xl:p-8 space-y-4 lg:space-y-6 animate-fadeIn" data-testid="catalog-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-3">
            <Package size={28} className="text-[#26788A]" />
            Produits & Services
          </h1>
          <p className="text-slate-500 mt-1 text-sm lg:text-base">{items.length} articles actifs</p>
        </div>
        <Button
          data-testid="new-catalog-item-btn"
          className="btn-primary w-full sm:w-auto"
          onClick={() => openDialog()}
        >
          <Plus size={16} className="mr-2" />
          Nouvel article
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input
            data-testid="search-catalog-input"
            placeholder="Rechercher un article..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 input-field"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-48 input-field">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="service">Services</SelectItem>
            <SelectItem value="product">Produits</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Item List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Chargement...</div>
      ) : items.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center">
            <p className="text-slate-500">Aucun article trouvé</p>
            <Button
              className="mt-4 btn-primary"
              onClick={() => openDialog()}
            >
              <Plus size={16} className="mr-2" />
              Créer votre premier article
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <Card 
              key={item.id} 
              className="bg-white border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
              data-testid={`catalog-item-${item.id}`}
              onClick={() => openDialog(item)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      {item.item_type === "service" ? (
                        <Wrench size={18} className="text-[#26788A]" />
                      ) : (
                        <Package size={18} className="text-[#26788A]" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{item.name}</p>
                      <p className="text-xs text-slate-500 font-mono uppercase">
                        {item.item_type === "service" ? "Service" : "Produit"}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-[#26788A]"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDialog(item);
                      }}
                    >
                      <Edit size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                
                <div className="mt-4">
                  {item.description && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                      {item.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 font-mono">
                      {UNIT_LABELS[item.unit]} • TVA {item.vat_rate}%
                    </span>
                    <span className="font-mono text-slate-900 font-semibold">
                      {formatCurrency(item.unit_price_ht)} HT
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Item Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-slate-900">
              {editingItem ? "Modifier l'article" : "Nouvel article"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Type
              </Label>
              <Select 
                value={form.item_type} 
                onValueChange={(v) => setForm({ ...form, item_type: v })}
              >
                <SelectTrigger className="mt-2 input-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="product">Produit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Nom *
              </Label>
              <Input
                data-testid="catalog-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 input-field"
                placeholder="Nom de l'article"
              />
            </div>

            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Description
              </Label>
              <Textarea
                data-testid="catalog-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-2 input-field min-h-20"
                placeholder="Description détaillée..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Unité
                </Label>
                <Select 
                  value={form.unit} 
                  onValueChange={(v) => setForm({ ...form, unit: v })}
                >
                  <SelectTrigger className="mt-2 input-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hour">Heure</SelectItem>
                    <SelectItem value="day">Jour</SelectItem>
                    <SelectItem value="flat">Forfait</SelectItem>
                    <SelectItem value="piece">Pièce</SelectItem>
                    <SelectItem value="month">Mois</SelectItem>
                    <SelectItem value="project">Projet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Taux TVA
                </Label>
                <Select 
                  value={String(form.vat_rate)} 
                  onValueChange={(v) => setForm({ ...form, vat_rate: parseFloat(v) })}
                >
                  <SelectTrigger className="mt-2 input-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20%</SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="5.5">5.5%</SelectItem>
                    <SelectItem value="2.1">2.1%</SelectItem>
                    <SelectItem value="0">0%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Prix unitaire HT (€)
              </Label>
              <Input
                data-testid="catalog-price-input"
                type="number"
                step="0.01"
                min="0"
                value={form.unit_price_ht}
                onChange={(e) => setForm({ ...form, unit_price_ht: parseFloat(e.target.value) || 0 })}
                className="mt-2 input-field font-mono"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-slate-200 text-slate-600"
            >
              Annuler
            </Button>
            <Button
              data-testid="save-catalog-item-btn"
              onClick={handleSubmit}
              className="btn-primary"
            >
              {editingItem ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogPage;
